import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment';
import qs from 'query-string';
import isEqual from 'lodash.isequal';
import { useTranslation } from 'react-i18next';
//
import getFiltersMeta from './filtersMeta.js';
import { useAppConfig } from '@state';
import { useDebounce, useSearchParams } from '../../hooks';
import { utils, Types as coreTypes } from '@ohif/core';

import {
  StudyListExpandedRow,
  EmptyStudies,
  StudyListTable,
  StudyListPagination,
  StudyListFilter,
  Button,
  ButtonEnums,
} from '@ohif/ui';

import {
  Icons,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  Clipboard,
  useModal,
  useSessionStorage,
  Onboarding,
} from '@ohif/ui-next';

import { PageHeader } from '../../custom/components/layouts';
import SidebarBackdrop from '../../custom/components/layouts/SidebarBackdrop';
import AppSidebar from '../../custom/components/AppSidebar/AppSidebar';
import '../../custom/components/AppSidebar/AppSidebar.css';
import '../../custom/theme/layout-shell.css';
import { ResponsiveLayoutProvider } from '../../custom/context/ResponsiveLayoutContext';
import { useMenuItems, useAuthUser } from '../../custom/hooks/useMenuItems';
import { mapMenuIconToSidebar } from '../../custom/utils/sidebarIconMap';
import AssignRadiologistModal from '../../custom/components/modals/AssignRadiologistModal';
import AssignPeerReviewerModal from '../../custom/components/modals/AssignPeerReviewerModal';
import AssignHubFacilityModal from '../../custom/components/modals/AssignHubFacilityModal';
import { facilityService } from '../../custom/services/facilityService';
import { authService } from '../../custom/auth/authService';
import type { LinkedHubFacilityDetail } from '../../custom/types/facility.types';
import {
  getImagingWorklistLayout,
  isTechnicianOrAdmin,
  showImagingWorklistAssignedColumn,
} from '../../custom/utils/roleUtils';
import {
  getHubSpokeWorklistColumnKey,
} from '../../custom/constants/imagingWorklistColumns';
import {
  getStudyViewerHref,
  IMAGING_MODALITY_OPTIONS,
  ImagingWorklistPagination,
  ImagingWorklistTable,
  ImagingWorklistToolbar,
  isAllowedImagingModalityCode,
  formatAppointmentTypeDisplay,
  loadImagingColumnVisibility,
  persistImagingColumnVisibility,
  PRIORITY_OPTIONS,
  REPORT_OPTIONS,
  AI_RESULT_OPTIONS,
} from '../../custom/components/worklist';

import { preserveQueryParameters, preserveQueryStrings } from '../../utils/preserveQueryParameters';
import {
  getSelectionHubAssignmentState,
  isHubReassignBlocked,
  selectionHasAssignedStudies,
} from './worklistSelectionUtils';
import { API_ENDPOINTS, STORAGE_KEYS } from '../../custom/config/api.config';
import { batchHubRoutingStudies, getAuthHeaders } from '../../custom/utils/studyAssignmentUtils';

import '../../custom/components/worklist/imagingWorklist.css';
import './workListResponsive.css';

const { sortBySeriesDate } = utils;

/** Read RIS imaging filter query params back into toolbar state */
function parseImagingListFiltersFromSearch(searchString) {
  const queryWithoutLeadingQuestion =
    searchString && searchString.startsWith('?') ? searchString.slice(1) : searchString || '';
  const urlParams = new URLSearchParams(queryWithoutLeadingQuestion);
  const reportStatusRaw = urlParams.get('report_status');
  const reportStatusNormalized =
    reportStatusRaw === 'Draft' ? 'Drafted' : reportStatusRaw;
  const reportStatus =
    reportStatusNormalized && REPORT_OPTIONS.includes(reportStatusNormalized)
      ? reportStatusNormalized
      : 'all';
  const priorityStatusRaw = urlParams.get('priority_status');
  const priority =
    priorityStatusRaw && PRIORITY_OPTIONS.includes(priorityStatusRaw) ? priorityStatusRaw : 'all';
  const studyStatusRaw = (urlParams.get('study_status') || '').toLowerCase();
  let studyScope = 'all';
  if (studyStatusRaw === 'assigned') {
    studyScope = 'Assigned';
  } else if (studyStatusRaw === 'unassigned') {
    studyScope = 'Unassigned';
  }
  const assignedIdRaw = (urlParams.get('assigned_id') || '').trim();
  const radiologistId = /^\d+$/.test(assignedIdRaw) ? assignedIdRaw : '';
  const aiResultRaw = (urlParams.get('ai_result') || '').trim().toLowerCase();
  const aiResult =
    aiResultRaw && AI_RESULT_OPTIONS.includes(aiResultRaw) ? aiResultRaw : 'all';
  return { reportStatus, priority, studyScope, radiologistId, aiResult };
}

const seriesInStudiesMap = new Map();

/**
 * TODO:
 * - debounce `setFilterValues` (150ms?)
 */
function WorkList({
  data: studies,
  dataTotal: studiesTotal,
  isLoadingData,
  dataSource,
  hotkeysManager,
  dataPath,
  onRefresh,
  servicesManager,
}: withAppTypes) {
  const { show, hide } = useModal();
  const { t } = useTranslation();
  const user = useAuthUser();
  const canAssignRadiologist = isTechnicianOrAdmin(user);
  const imagingLayout = getImagingWorklistLayout(user);
  const showAssignedWorklistColumn = showImagingWorklistAssignedColumn(user);
  const isImagingWorklist = imagingLayout !== 'classic';
  // ~ Modes
  const [appConfig] = useAppConfig();
  // ~ Filters
  const filtersMeta = useMemo(() => getFiltersMeta(canAssignRadiologist), [canAssignRadiologist]);
  const searchParams = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const STUDIES_LIMIT = 101;
  const queryFilterValues = _getQueryFilterValues(searchParams);
  const [sessionQueryFilterValues, updateSessionQueryFilterValues] = useSessionStorage({
    key: 'queryFilterValues',
    defaultValue: queryFilterValues,
    // ToDo: useSessionStorage currently uses an unload listener to clear the filters from session storage
    // so on systems that do not support unload events a user will NOT be able to alter any existing filter
    // in the URL, load the page and have it apply.
    clearOnUnload: true,
  });
  const [filterValues, _setFilterValues] = useState({
    ...defaultFilterValues,
    ...sessionQueryFilterValues,
  });

  const debouncedFilterValues = useDebounce(filterValues, 200);
  const { resultsPerPage, pageNumber, sortBy, sortDirection } = filterValues;

  /*
   * The default sort value keep the filters synchronized with runtime conditional sorting
   * Only applied if no other sorting is specified and there are less than 101 studies
   */

  const canSort = studiesTotal < STUDIES_LIMIT;
  const shouldUseDefaultSort = sortBy === '' || !sortBy;
  const sortModifier = sortDirection === 'descending' ? 1 : -1;
  const defaultSortValues =
    shouldUseDefaultSort && canSort ? { sortBy: 'studyDate', sortDirection: 'ascending' } : {};
  const { customizationService } = servicesManager.services;

  const sortedStudies = useMemo(() => {
    /** Imaging worklist order comes from RIS worklist priority rules (server-side). */
    if (isImagingWorklist && shouldUseDefaultSort) {
      return studies;
    }

    if (!canSort) {
      return studies;
    }

    return [...studies].sort((leftStudy, rightStudy) => {
      if (shouldUseDefaultSort) {
        const ascendingSortModifier = -1;
        return _sortStringDates(leftStudy, rightStudy, ascendingSortModifier);
      }

      const leftSortValue = leftStudy[sortBy];
      const rightSortValue = rightStudy[sortBy];

      if (typeof leftSortValue === 'string' && typeof rightSortValue === 'string') {
        return leftSortValue.localeCompare(rightSortValue) * sortModifier;
      } else if (typeof leftSortValue === 'number' && typeof rightSortValue === 'number') {
        return (leftSortValue > rightSortValue ? 1 : -1) * sortModifier;
      } else if (!leftSortValue && rightSortValue) {
        return -1 * sortModifier;
      } else if (!rightSortValue && leftSortValue) {
        return 1 * sortModifier;
      } else if (sortBy === 'studyDate') {
        return _sortStringDates(leftStudy, rightStudy, sortModifier);
      }

      return 0;
    });
  }, [canSort, studies, shouldUseDefaultSort, sortBy, sortModifier, isImagingWorklist]);

  const [imagingListFilters, setImagingListFilters] = useState({
    reportStatus: 'all',
    priority: 'all',
    studyScope: 'all',
    radiologistId: '',
    aiResult: 'all',
  });
  const debouncedImagingListFilters = useDebounce(imagingListFilters, 200);

  const [columnVisibility, setColumnVisibility] = useState(() =>
    loadImagingColumnVisibility(
      getImagingWorklistLayout(user),
      showImagingWorklistAssignedColumn(user),
      null
    )
  );

  const studiesAfterClientFilters = useMemo(() => {
    if (!isImagingWorklist) {
      return sortedStudies;
    }
    /** Toolbar filters are sent as RIS query params; avoid double-filtering client-side */
    return sortedStudies;
  }, [sortedStudies, isImagingWorklist]);

  /** RIS returns one page of rows + totalItems; legacy OHIF loads a chunk and pages client-side */
  const usesServerStudyPagination = useMemo(
    () =>
      isImagingWorklist && typeof studiesTotal === 'number' && studiesTotal > sortedStudies.length,
    [isImagingWorklist, studiesTotal, sortedStudies.length]
  );

  /** Radiologist filter: GET /ris/api/users?role=Radiologist — id + full_name for dropdown; studies API uses `assigned_id`. */
  const [radiologistOptionsFromApi, setRadiologistOptionsFromApi] = useState<
    { id: number; fullName: string }[]
  >([]);

  useEffect(() => {
    if (!isImagingWorklist || imagingLayout !== 'adminTech') {
      setRadiologistOptionsFromApi([]);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const facilityId = localStorage.getItem(STORAGE_KEYS.FACILITY_ID);
        const params: Parameters<typeof API_ENDPOINTS.RADIOLOGISTS.GET>[0] = {
          page: 1,
          limit: 100,
          role: 'Radiologist',
          status: 'Active',
        };
        if (facilityId != null && facilityId !== '') {
          const facilityIdNumber = parseInt(facilityId, 10);
          if (!Number.isNaN(facilityIdNumber)) {
            params.facility_id = facilityIdNumber;
          }
        }

        const url = API_ENDPOINTS.RADIOLOGISTS.GET(params);
        const response = await fetch(url, { method: 'GET', headers: getAuthHeaders() });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const responseData = await response.json();
        const options: { id: number; fullName: string }[] =
          responseData.success && Array.isArray(responseData.data)
            ? responseData.data
                .map((apiUser: { id?: number; full_name?: string }) => ({
                  id: Number(apiUser.id),
                  fullName: String(apiUser.full_name || '').trim(),
                }))
                .filter(
                  radiologistOption =>
                    !Number.isNaN(radiologistOption.id) && radiologistOption.fullName !== ''
                )
            : [];
        if (!cancelled) {
          options.sort((left, right) => left.fullName.localeCompare(right.fullName));
          setRadiologistOptionsFromApi(options);
        }
      } catch (err) {
        console.error('Imaging worklist: failed to load radiologists for filter', err);
        if (!cancelled) {
          setRadiologistOptionsFromApi([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isImagingWorklist, imagingLayout]);

  const [linkedHubFacilities, setLinkedHubFacilities] = useState<LinkedHubFacilityDetail[]>([]);
  const [canAssignToHubFacility, setCanAssignToHubFacility] = useState(false);
  const [userFacilityType, setUserFacilityType] = useState<string | null>(null);

  useEffect(() => {
    if (!isImagingWorklist || imagingLayout !== 'adminTech' || !canAssignRadiologist) {
      setLinkedHubFacilities([]);
      setCanAssignToHubFacility(false);
      setUserFacilityType(null);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const facilityId = authService.getFacilityId();
        if (!facilityId) {
          if (!cancelled) {
            setLinkedHubFacilities([]);
            setCanAssignToHubFacility(false);
            setUserFacilityType(null);
          }
          return;
        }

        const response = await facilityService.getFacilityById(facilityId);
        if (cancelled) {
          return;
        }

        if (!response.success || !response.data) {
          setLinkedHubFacilities([]);
          setCanAssignToHubFacility(false);
          setUserFacilityType(null);
          return;
        }

        const facility = response.data;
        const facilityType = String(facility.facility_type || '').toUpperCase();
        const hubIds = facility.linked_hub_facility_ids ?? [];
        const hubDetails = facility.linked_hub_facility_details ?? [];
        const isSpoke = facilityType === 'SPOKE';
        const hasMultipleHubs = hubIds.length > 1;

        setUserFacilityType(facilityType);
        setCanAssignToHubFacility(isSpoke && hasMultipleHubs);
        setLinkedHubFacilities(
          hubDetails.length > 0
            ? hubDetails
            : hubIds.map(id => ({ id, name: `Hub ${id}` }))
        );
      } catch (err) {
        console.error('Imaging worklist: failed to load facility for hub assignment', err);
        if (!cancelled) {
          setLinkedHubFacilities([]);
          setCanAssignToHubFacility(false);
          setUserFacilityType(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isImagingWorklist, imagingLayout, canAssignRadiologist]);

  const hubSpokeWorklistColumnKey = useMemo(
    () => getHubSpokeWorklistColumnKey(userFacilityType),
    [userFacilityType]
  );

  useEffect(() => {
    if (!isImagingWorklist) {
      return;
    }

    setColumnVisibility(
      loadImagingColumnVisibility(
        imagingLayout,
        showAssignedWorklistColumn,
        hubSpokeWorklistColumnKey
      )
    );
  }, [isImagingWorklist, imagingLayout, showAssignedWorklistColumn, hubSpokeWorklistColumnKey]);

  const modalityOptions = useMemo(() => {
    if (isImagingWorklist) {
      return IMAGING_MODALITY_OPTIONS;
    }
    const modalityField = filtersMeta.find(filterMeta => filterMeta.name === 'modalities');
    return modalityField?.inputProps?.options || [];
  }, [filtersMeta, isImagingWorklist]);

  // ~ Rows & Studies
  const [expandedRows, setExpandedRows] = useState([]);
  const [studiesWithSeriesData, setStudiesWithSeriesData] = useState([]);
  const [checkedStudies, setCheckedStudies] = useState(new Set());
  const openAssignModalRef = useRef(() => {});
  const openAssignPeerReviewModalRef = useRef(() => {});
  const openAssignHubModalRef = useRef(() => {});
  const unassignHubStudiesRef = useRef(async () => {});
  const unassignStudiesRef = useRef(async () => {});
  const hubFacilityNameById = useMemo(() => {
    const nameById: Record<string, string> = {};
    linkedHubFacilities.forEach(hubFacility => {
      nameById[String(hubFacility.id)] = hubFacility.name;
    });
    return nameById;
  }, [linkedHubFacilities]);
  const numOfStudies = studiesTotal;
  const querying = useMemo(() => {
    return isLoadingData || expandedRows.length > 0;
  }, [isLoadingData, expandedRows]);

  const setFilterValues = val => {
    if (filterValues.pageNumber === val.pageNumber) {
      val.pageNumber = 1;
    }
    _setFilterValues(val);
    updateSessionQueryFilterValues(val);
    setExpandedRows([]);
  };

  const onPageNumberChange = newPageNumber => {
    if (
      isImagingWorklist &&
      typeof studiesTotal === 'number' &&
      studiesTotal > sortedStudies.length
    ) {
      const totalPages = Math.max(1, Math.ceil(studiesTotal / resultsPerPage));
      if (newPageNumber < 1 || newPageNumber > totalPages) {
        return;
      }
      setFilterValues({ ...filterValues, pageNumber: newPageNumber });
      return;
    }

    const oldPageNumber = filterValues.pageNumber;
    const rollingPageNumberMod = Math.floor(101 / filterValues.resultsPerPage);
    const rollingPageNumber = oldPageNumber % rollingPageNumberMod;
    const isNextPage = newPageNumber > oldPageNumber;
    const effectiveTotal = isImagingWorklist ? studiesAfterClientFilters.length : numOfStudies;
    const hasNextPage = Math.max(rollingPageNumber, 1) * resultsPerPage < effectiveTotal;

    if (isNextPage && !hasNextPage) {
      return;
    }

    setFilterValues({ ...filterValues, pageNumber: newPageNumber });
  };

  const onResultsPerPageChange = newResultsPerPage => {
    setFilterValues({
      ...filterValues,
      pageNumber: 1,
      resultsPerPage: Number(newResultsPerPage),
    });
  };

  // Imaging route: override global `body { !bg-black }` (see imagingWorklist.css)
  useEffect(() => {
    if (isImagingWorklist) {
      document.body.classList.add('ohif-imaging-worklist-route');
    } else {
      document.body.classList.remove('ohif-imaging-worklist-route');
    }
    return () => {
      document.body.classList.remove('ohif-imaging-worklist-route');
    };
  }, [isImagingWorklist]);

  useEffect(() => {
    if (isImagingWorklist) {
      setExpandedRows([]);
    }
  }, [imagingListFilters, isImagingWorklist]);

  /** RIS imaging worklist filters (report_status, modality_name, etc.) — not server page/pageSize. */
  useLayoutEffect(() => {
    if (!isImagingWorklist) {
      return;
    }
    const locationSearchParams = new URLSearchParams(window.location.search);
    if (locationSearchParams.get('risstudypagination') === '1') {
      return;
    }
    locationSearchParams.set('risstudypagination', '1');
    const nextSearchString = locationSearchParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearchString ? `?${nextSearchString}` : '?risstudypagination=1',
      },
      { replace: true }
    );
  }, [isImagingWorklist, location.pathname, navigate]);

  /** Hydrate imaging toolbar + modality chips when URL changes (bookmark, back/forward). */
  useEffect(() => {
    if (!isImagingWorklist) {
      return;
    }
    setImagingListFilters(prev => {
      const toolbarStateFromUrl = parseImagingListFiltersFromSearch(location.search);
      return isEqual(prev, toolbarStateFromUrl) ? prev : toolbarStateFromUrl;
    });
    const hydrateParams = new URLSearchParams(
      location.search.startsWith('?') ? location.search.slice(1) : location.search
    );
    const modalityNameParam = hydrateParams.get('modality_name');
    if (modalityNameParam) {
      const allowedModalityCodesFromUrl = modalityNameParam
        .split(/[/,\\]+/)
        .map(token => token.trim().toUpperCase())
        .filter(isAllowedImagingModalityCode);
      _setFilterValues(prev => {
        if (isEqual(prev.modalities, allowedModalityCodesFromUrl)) {
          return prev;
        }
        const nextFilterValues = { ...prev, modalities: allowedModalityCodesFromUrl };
        updateSessionQueryFilterValues(nextFilterValues);
        return nextFilterValues;
      });
    }
  }, [location.search, isImagingWorklist]);

  /**
   * Legacy URLs used `assigned_name`; after toolbar hydrates from URL, map name → user id so QIDO uses `assigned_id`.
   * Runs after the hydrate effect above so this update is not overwritten.
   */
  useEffect(() => {
    if (!isImagingWorklist || imagingLayout !== 'adminTech') {
      return;
    }
    const legacyQuery = location.search.startsWith('?') ? location.search.slice(1) : location.search;
    const legacyUrlParams = new URLSearchParams(legacyQuery);
    if (legacyUrlParams.get('assigned_id')) {
      return;
    }
    const legacyAssignedName = (legacyUrlParams.get('assigned_name') || '').trim();
    if (!legacyAssignedName || radiologistOptionsFromApi.length === 0) {
      return;
    }
    setImagingListFilters(prev => {
      if (prev.radiologistId) {
        return prev;
      }
      const matchedRadiologist = radiologistOptionsFromApi.find(
        radiologistOption =>
          radiologistOption.fullName.trim().toLowerCase() === legacyAssignedName.toLowerCase()
      );
      if (!matchedRadiologist) {
        return prev;
      }
      return { ...prev, radiologistId: String(matchedRadiologist.id) };
    });
  }, [isImagingWorklist, imagingLayout, location.search, radiologistOptionsFromApi]);

  // Sync URL query parameters with filters
  useEffect(() => {
    if (!debouncedFilterValues) {
      return;
    }

    const queryString: Record<string, string | number | boolean> = {};
    if (isImagingWorklist) {
      queryString.risstudypagination = 1;
      const debouncedToolbarFilters = debouncedImagingListFilters;
      if (debouncedToolbarFilters.reportStatus && debouncedToolbarFilters.reportStatus !== 'all') {
        queryString.report_status = debouncedToolbarFilters.reportStatus;
      }
      if (debouncedToolbarFilters.priority && debouncedToolbarFilters.priority !== 'all') {
        queryString.priority_status = debouncedToolbarFilters.priority;
      }
      if (debouncedToolbarFilters.aiResult && debouncedToolbarFilters.aiResult !== 'all') {
        queryString.ai_result = debouncedToolbarFilters.aiResult;
      }
      if (debouncedToolbarFilters.studyScope === 'Assigned') {
        queryString.study_status = 'assigned';
      } else if (debouncedToolbarFilters.studyScope === 'Unassigned') {
        queryString.study_status = 'unassigned';
      }
      if (imagingLayout === 'adminTech' && debouncedToolbarFilters.radiologistId) {
        queryString.assigned_id = debouncedToolbarFilters.radiologistId;
      }
      const risModalityFilterCodes = (debouncedFilterValues.modalities || [])
        .map((modalityCode: string) => String(modalityCode).trim().toUpperCase())
        .filter(isAllowedImagingModalityCode);
      if (risModalityFilterCodes.length) {
        queryString.modality_name = risModalityFilterCodes.join(',');
      }
    }
    Object.keys(defaultFilterValues).forEach(key => {
      const defaultValue = defaultFilterValues[key];
      const currValue = debouncedFilterValues[key];

      // TODO: nesting/recursion?
      if (key === 'studyDate') {
        if (currValue.startDate && defaultValue.startDate !== currValue.startDate) {
          queryString.startDate = currValue.startDate;
        }
        if (currValue.endDate && defaultValue.endDate !== currValue.endDate) {
          queryString.endDate = currValue.endDate;
        }
      } else if (key === 'modalities' && currValue.length) {
        queryString.modalities = currValue.join(',');
      } else if (currValue !== defaultValue) {
        queryString[key] = currValue;
      }
    });

    preserveQueryStrings(queryString);

    const search = qs.stringify(queryString, {
      skipNull: true,
      skipEmptyString: true,
    });
    navigate({
      pathname: location.pathname,
      search: search ? `?${search}` : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilterValues, debouncedImagingListFilters, isImagingWorklist, imagingLayout, location.pathname]);

  // Query for series information
  useEffect(() => {
    const fetchSeries = async studyInstanceUid => {
      try {
        const series = await dataSource.query.series.search(studyInstanceUid);
        seriesInStudiesMap.set(studyInstanceUid, sortBySeriesDate(series));
        setStudiesWithSeriesData([...studiesWithSeriesData, studyInstanceUid]);
      } catch (ex) {
        // TODO: UI Notification Service
        console.warn(ex);
      }
    };

    // TODO: WHY WOULD YOU USE AN INDEX OF 1?!
    // Note: expanded rows index begins at 1
    const studiesForRowIndex = isImagingWorklist ? studiesAfterClientFilters : sortedStudies;

    for (let expandedIndex = 0; expandedIndex < expandedRows.length; expandedIndex++) {
      const expandedRowIndex = expandedRows[expandedIndex] - 1;
      const study = studiesForRowIndex[expandedRowIndex];

      // Safety check: ensure study exists before accessing its properties
      if (!study || !study.studyInstanceUid) {
        continue;
      }

      const studyInstanceUid = study.studyInstanceUid;

      if (studiesWithSeriesData.includes(studyInstanceUid)) {
        continue;
      }

      fetchSeries(studyInstanceUid);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedRows, studies, isImagingWorklist, studiesAfterClientFilters, sortedStudies]);

  const isFiltering = (filterValues, defaultFilterValues) => {
    return !isEqual(filterValues, defaultFilterValues);
  };

  const rollingPageNumberMod = Math.floor(101 / resultsPerPage);
  const rollingPageNumber = (pageNumber - 1) % rollingPageNumberMod;
  const offset = resultsPerPage * rollingPageNumber;
  const offsetAndTake = offset + resultsPerPage;
  const imagingTablePageOffset = usesServerStudyPagination
    ? resultsPerPage * (pageNumber - 1)
    : offset;
  const imagingDisplayStudies = isImagingWorklist
    ? usesServerStudyPagination
      ? studiesAfterClientFilters
      : studiesAfterClientFilters.slice(offset, offsetAndTake)
    : [];

  const hubAssignmentSelectionState = useMemo(() => {
    if (checkedStudies.size === 0) {
      return null;
    }
    const selectedStudies = sortedStudies.filter(study =>
      checkedStudies.has(study.studyInstanceUid)
    );
    return getSelectionHubAssignmentState(selectedStudies);
  }, [sortedStudies, checkedStudies]);

  /** Peer review assign is available only when every selected study has Approved report status. */
  const selectionIsAllApprovedForPeerReview = useMemo(() => {
    if (checkedStudies.size === 0) {
      return false;
    }
    const selectedStudies = sortedStudies.filter(study =>
      checkedStudies.has(study.studyInstanceUid)
    );
    if (selectedStudies.length === 0) {
      return false;
    }
    return selectedStudies.every(
      study => study.reportStatus?.trim().toLowerCase() === 'approved'
    );
  }, [sortedStudies, checkedStudies]);

  const tableDataSource = sortedStudies.map((study, key) => {
    const rowKey = key + 1;
    const isExpanded = expandedRows.some(k => k === rowKey);
    const {
      studyInstanceUid,
      accession,
      modalities,
      instances,
      description,
      mrn,
      patientName,
      date,
      time,
      reportStatus,
      assignedName,
      isCritical,
      criticalReason,
      referringPhysician,
      appointmentType,
    } = study;
    const studyDate =
      date &&
      moment(date, ['YYYYMMDD', 'YYYY.MM.DD'], true).isValid() &&
      moment(date, ['YYYYMMDD', 'YYYY.MM.DD']).format(t('Common:localDateFormat', 'MMM-DD-YYYY'));
    const studyTime =
      time &&
      moment(time, ['HH', 'HHmm', 'HHmmss', 'HHmmss.SSS']).isValid() &&
      moment(time, ['HH', 'HHmm', 'HHmmss', 'HHmmss.SSS']).format(
        t('Common:localTimeFormat', 'hh:mm A')
      );

    const makeCopyTooltipCell = textValue => {
      if (!textValue) {
        return '';
      }
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-pointer truncate">{textValue}</span>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <div className="flex items-center justify-between gap-2">
              {textValue}
              <Clipboard>{textValue}</Clipboard>
            </div>
          </TooltipContent>
        </Tooltip>
      );
    };

    return {
      dataCY: `studyRow-${studyInstanceUid}`,
      clickableCY: studyInstanceUid,
      row: [
        {
          key: 'patientName',
          content: (
            <div className="flex items-center gap-2">
              {isCritical && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex cursor-pointer">
                      <Icons.StatusWarning className="flex-shrink-0 text-red-500" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent
                    side="right"
                    className="max-w-xs"
                  >
                    <div className="space-y-1">
                      <div className="font-semibold text-white">Critical Case</div>
                      {criticalReason && criticalReason.trim() ? (
                        <div className="whitespace-normal break-words text-sm text-gray-200">
                          {criticalReason}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-300">
                          This case has been marked as critical
                        </div>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              )}
              {patientName ? makeCopyTooltipCell(patientName) : null}
            </div>
          ),
          gridCol: 4,
        },
        {
          key: 'mrn',
          content: makeCopyTooltipCell(mrn),
          gridCol: 3,
        },
        {
          key: 'studyDate',
          content: (
            <>
              {studyDate && <span className="mr-4">{studyDate}</span>}
              {studyTime && <span>{studyTime}</span>}
            </>
          ),
          title: `${studyDate || ''} ${studyTime || ''}`,
          gridCol: 5,
        },
        {
          key: 'description',
          content: makeCopyTooltipCell(description),
          gridCol: 4,
        },
        {
          key: 'modality',
          content: modalities,
          title: modalities,
          gridCol: 2,
        },
        {
          key: 'accession',
          content: makeCopyTooltipCell(accession),
          gridCol: 2,
        },
        {
          key: 'referringPhysician',
          content:
            referringPhysician && String(referringPhysician).trim()
              ? makeCopyTooltipCell(String(referringPhysician).trim())
              : '',
          title:
            referringPhysician && String(referringPhysician).trim()
              ? String(referringPhysician).trim()
              : '',
          gridCol: 3,
        },
        {
          key: 'appointmentType',
          content: makeCopyTooltipCell(formatAppointmentTypeDisplay(appointmentType)),
          title: formatAppointmentTypeDisplay(appointmentType),
          gridCol: 2,
        },
        ...(canAssignRadiologist
          ? [
              {
                key: 'assignStatus',
                content: makeCopyTooltipCell(
                  assignedName && assignedName.trim() ? assignedName : 'Unassigned'
                ),
                gridCol: 3,
              },
            ]
          : []),
        {
          key: 'reportStatus',
          content: makeCopyTooltipCell(reportStatus || ''),
          gridCol: 3,
        },
        {
          key: 'instances',
          content: (
            <>
              <Icons.GroupLayers
                className={classnames('mr-2 inline-flex w-4', {
                  'text-primary': isExpanded,
                  'text-secondary-light': !isExpanded,
                })}
              />
              {instances}
            </>
          ),
          title: (instances || 0).toString(),
          gridCol: 2,
        },
      ],
      // Todo: This is actually running for all rows, even if they are
      // not clicked on.
      expandedContent: (
        <StudyListExpandedRow
          seriesTableColumns={{
            description: t('StudyList:Description'),
            seriesNumber: t('StudyList:Series'),
            modality: t('StudyList:Modality'),
            instances: t('StudyList:Instances'),
          }}
          seriesTableDataSource={
            seriesInStudiesMap.has(studyInstanceUid)
              ? seriesInStudiesMap.get(studyInstanceUid).map(s => {
                  return {
                    description: s.description || '(empty)',
                    seriesNumber: s.seriesNumber ?? '',
                    modality: s.modality || '',
                    instances: s.numSeriesInstances || '',
                  };
                })
              : []
          }
        >
          <div className="flex flex-row gap-2">
            {(appConfig.groupEnabledModesFirst
              ? appConfig.loadedModes.sort((leftMode, rightMode) => {
                  const leftValid = leftMode.isValidMode({
                    modalities: modalities.replaceAll('/', '\\'),
                    study,
                  }).valid;
                  const rightValid = rightMode.isValidMode({
                    modalities: modalities.replaceAll('/', '\\'),
                    study,
                  }).valid;

                  return rightValid - leftValid;
                })
              : appConfig.loadedModes
            ).map((mode, i) => {
              const modalitiesToCheck = modalities.replaceAll('/', '\\');

              const { valid: isValidMode, description: invalidModeDescription } = mode.isValidMode({
                modalities: modalitiesToCheck,
                study,
              });
              // TODO: Modes need a default/target route? We mostly support a single one for now.
              // We should also be using the route path, but currently are not
              // mode.routeName
              // mode.routes[x].path
              // Don't specify default data source, and it should just be picked up... (this may not currently be the case)
              // How do we know which params to pass? Today, it's just StudyInstanceUIDs and configUrl if exists
              const query = new URLSearchParams();
              if (filterValues.configUrl) {
                query.append('configUrl', filterValues.configUrl);
              }
              query.append('StudyInstanceUIDs', studyInstanceUid);
              preserveQueryParameters(query);

              return (
                mode.displayName && (
                  <Link
                    className={isValidMode ? '' : 'cursor-not-allowed'}
                    key={i}
                    to={`${mode.routeName}${dataPath || ''}?${query.toString()}`}
                    onClick={event => {
                      // In case any event bubbles up for an invalid mode, prevent the navigation.
                      // For example, the event bubbles up when the icon embedded in the disabled button is clicked.
                      if (!isValidMode) {
                        event.preventDefault();
                      }
                    }}
                    // to={`${mode.routeName}/dicomweb?StudyInstanceUIDs=${studyInstanceUid}`}
                  >
                    {/* TODO revisit the completely rounded style of buttons used for launching a mode from the worklist later */}
                    <Button
                      type={ButtonEnums.type.primary}
                      size={ButtonEnums.size.medium}
                      disabled={!isValidMode}
                      startIconTooltip={
                        !isValidMode ? (
                          <div className="font-inter flex w-[206px] whitespace-normal text-left text-xs font-normal text-white">
                            {invalidModeDescription}
                          </div>
                        ) : null
                      }
                      startIcon={
                        isValidMode ? (
                          <Icons.LaunchArrow className="!h-[20px] !w-[20px] text-black" />
                        ) : (
                          <Icons.LaunchInfo className="!h-[20px] !w-[20px] text-black" />
                        )
                      }
                      onClick={() => {}}
                      dataCY={`mode-${mode.routeName}-${studyInstanceUid}`}
                      className={isValidMode ? 'text-[13px]' : 'bg-[#222d44] text-[13px]'}
                    >
                      {mode.displayName}
                    </Button>
                  </Link>
                )
              );
            })}
          </div>
        </StudyListExpandedRow>
      ),
      onClickRow: () =>
        setExpandedRows(s => (isExpanded ? s.filter(n => rowKey !== n) : [...s, rowKey])),
      isExpanded,
      isChecked: checkedStudies.has(studyInstanceUid),
      onCheckboxChange: checked => {
        setCheckedStudies(prev => {
          const newSet = new Set(prev);
          if (checked) {
            newSet.add(studyInstanceUid);
          } else {
            newSet.delete(studyInstanceUid);
          }
          return newSet;
        });
      },
      showCheckbox: canAssignRadiologist,
      isCritical: isCritical || false,
      criticalReason: criticalReason || '',
    };
  });

  const mergeWorklistServerFilters = patch => {
    setFilterValues({ ...filterValues, ...patch });
  };

  const handleImagingListFiltersChange = next => {
    setImagingListFilters(next);
    mergeWorklistServerFilters({ pageNumber: 1 });
    setExpandedRows([]);
  };

  const hasStudies = isImagingWorklist ? studiesAfterClientFilters.length > 0 : numOfStudies > 0;

  const AboutModal = customizationService.getCustomization(
    'ohif.aboutModal'
  ) as coreTypes.MenuComponentCustomization;
  const UserPreferencesModal = customizationService.getCustomization(
    'ohif.userPreferencesModal'
  ) as coreTypes.MenuComponentCustomization;

  const menuOptions = [
    {
      title: AboutModal?.menuTitle ?? t('Header:About'),
      icon: 'info',
      onClick: () =>
        show({
          content: AboutModal,
          title: AboutModal?.title ?? t('AboutModal:About OHIF Viewer'),
          containerClassName: AboutModal?.containerClassName ?? 'max-w-md',
        }),
    },
    {
      title: UserPreferencesModal.menuTitle ?? t('Header:Preferences'),
      icon: 'settings',
      onClick: () =>
        show({
          content: UserPreferencesModal as React.ComponentType,
          title: UserPreferencesModal.title ?? t('UserPreferencesModal:User preferences'),
          containerClassName:
            UserPreferencesModal?.containerClassName ?? 'flex max-w-4xl p-6 flex-col',
          showCloseButton: false,
        }),
    },
  ];

  if (appConfig.oidc) {
    menuOptions.push({
      icon: 'power-off',
      title: t('Header:Logout'),
      onClick: () => {
        navigate(`/logout?redirect_uri=${encodeURIComponent(window.location.href)}`);
      },
    });
  }

  const LoadingIndicatorProgress = customizationService.getCustomization(
    'ui.loadingIndicatorProgress'
  );
  const DicomUploadComponent = customizationService.getCustomization('dicomUploadComponent');

  const uploadProps =
    DicomUploadComponent && dataSource.getConfig()?.dicomUploadEnabled
      ? {
          title: 'Upload files',
          closeButton: true,
          shouldCloseOnEsc: false,
          shouldCloseOnOverlayClick: false,
          content: () => (
            <DicomUploadComponent
              dataSource={dataSource}
              onComplete={() => {
                hide();
                onRefresh();
              }}
              onStarted={() => {
                show({
                  ...uploadProps,
                  // when upload starts, hide the default close button as closing the dialogue must be handled by the upload dialogue itself
                  closeButton: false,
                });
              }}
            />
          ),
        }
      : undefined;

  const dataSourceConfigurationComponent = customizationService.getCustomization(
    'ohif.dataSourceConfigurationComponent'
  );

  // Get filtered menu items based on user roles using centralized hook
  const customMenuItems = useMenuItems();
  const sidebarMenuItems = customMenuItems.map(item => ({
    title: item.title,
    path: item.path,
    icon: mapMenuIconToSidebar(item.icon),
  }));

  openAssignModalRef.current = () => {
    const selectedStudies = sortedStudies.filter(study =>
      checkedStudies.has(study.studyInstanceUid)
    );

    const approvedStudies = selectedStudies.filter(
      study => study.reportStatus && study.reportStatus.trim().toLowerCase() === 'approved'
    );

    if (approvedStudies.length > 0) {
      const { uiNotificationService } = servicesManager.services;
      if (uiNotificationService) {
        uiNotificationService.show({
          title: t('Validation Error'),
          message: t(
            'This action cannot be performed on mixed selections. Please choose either Assigned or Unassigned studies.'
          ),
          type: 'error',
          duration: 5000,
          allowDuplicates: true,
        });
      }
      return;
    }

    const assignedCount = selectedStudies.filter(
      study => study.assignedName && study.assignedName.trim() !== ''
    ).length;
    const unassignedCount = selectedStudies.length - assignedCount;

    if (assignedCount > 0 && unassignedCount > 0) {
      const { uiNotificationService } = servicesManager.services;
      if (uiNotificationService) {
        uiNotificationService.show({
          title: t('Validation Error'),
          message: t(
            'This action cannot be performed on mixed selections. Please choose either Assigned or Unassigned studies.'
          ),
          type: 'error',
          duration: 5000,
        });
      }
      return;
    }

    const reportStatuses = selectedStudies
      .map(study => {
        const status = study.reportStatus?.trim() || '';
        return status.toLowerCase();
      })
      .filter(status => status !== '');

    const hasPending = reportStatuses.some(status => status === 'pending');
    const hasAwaitingReview = reportStatuses.some(status => status === 'awaiting review');

    if (hasPending && hasAwaitingReview) {
      const { uiNotificationService } = servicesManager.services;
      if (uiNotificationService) {
        uiNotificationService.show({
          title: t('Validation Error'),
          message: t(
            'Cannot assign studies with mixed status. Please select studies with either "Pending" or "Awaiting Review" status, not both.'
          ),
          type: 'error',
          duration: 5000,
        });
      }
      return;
    }

    show({
      content: AssignRadiologistModal,
      contentProps: {
        checkedStudyIds: Array.from(checkedStudies),
        selectedStudies: selectedStudies.map(study => ({
          studyInstanceUid: study.studyInstanceUid,
          modalities: study.modalities || '',
          assignedName: study.assignedName || '',
          sourceFacilityId: study.sourceFacilityId || null,
          studyId: study.studyId || null,
          reportStatus: study.reportStatus || '',
          patientName: study.patientName,
          studyDescription: study.description,
          patientId: study.mrn,
          accessionNumber: study.accession,
          priority: study.priority,
          isCritical: study.isCritical,
        })),
        servicesManager: servicesManager,
        onAssign: () => {},
        onSuccess: () => {
          setCheckedStudies(new Set());
          if (onRefresh) {
            onRefresh();
          }
        },
        onClose: () => hide(),
      },
      title: '',
      containerClassName:
        'max-w-lg w-[calc(100vw-2rem)] !bg-white p-0 gap-0 rounded-lg border border-[#E0E0E0] text-[#000000] shadow-lg sm:max-w-[520px]',
    });
  };

  openAssignPeerReviewModalRef.current = () => {
    const selectedStudies = sortedStudies.filter(study =>
      checkedStudies.has(study.studyInstanceUid)
    );

    if (selectedStudies.length === 0) {
      return;
    }

    const { uiNotificationService } = servicesManager.services;

    const nonApproved = selectedStudies.filter(
      study => study.reportStatus?.trim().toLowerCase() !== 'approved'
    );
    if (nonApproved.length > 0) {
      if (uiNotificationService) {
        uiNotificationService.show({
          title: t('Validation Error'),
          message: t(
            'Peer review can only be assigned to studies with an Approved report status. Please select only Approved studies.'
          ),
          type: 'error',
          duration: 5000,
          allowDuplicates: true,
        });
      }
      return;
    }

    show({
      content: AssignPeerReviewerModal,
      contentProps: {
        checkedStudyIds: Array.from(checkedStudies),
        selectedStudies: selectedStudies.map(study => ({
          studyInstanceUid: study.studyInstanceUid,
          modalities: study.modalities || '',
          assignedName: study.assignedName || '',
          sourceFacilityId: study.sourceFacilityId || null,
          studyId: study.studyId || null,
          reportStatus: study.reportStatus || '',
          patientName: study.patientName,
          studyDescription: study.description,
          patientId: study.mrn,
          accessionNumber: study.accession,
          priority: study.priority,
          isCritical: study.isCritical,
        })),
        servicesManager: servicesManager,
        onSuccess: () => {
          setCheckedStudies(new Set());
          if (onRefresh) {
            onRefresh();
          }
        },
        onClose: () => hide(),
      },
      title: '',
      containerClassName:
        'max-w-lg w-[calc(100vw-2rem)] !bg-white p-0 gap-0 rounded-lg border border-[#E0E0E0] text-[#000000] shadow-lg sm:max-w-[520px]',
    });
  };

  openAssignHubModalRef.current = () => {
    const selectedStudies = sortedStudies.filter(study =>
      checkedStudies.has(study.studyInstanceUid)
    );

    if (selectedStudies.length === 0) {
      return;
    }

    const hubAssignmentState = getSelectionHubAssignmentState(selectedStudies);
    const { uiNotificationService } = servicesManager.services;

    if (hubAssignmentState === 'mixed') {
      if (uiNotificationService) {
        uiNotificationService.show({
          title: t('Validation Error'),
          message: t(
            'This action cannot be performed on mixed selections. Please choose either hub-assigned or hub-unassigned studies.'
          ),
          type: 'error',
          duration: 5000,
        });
      }
      return;
    }

    const isReassign = hubAssignmentState === 'all';
    if (isReassign) {
      const blockedStudies = selectedStudies.filter(isHubReassignBlocked);
      if (blockedStudies.length > 0) {
        if (uiNotificationService) {
          uiNotificationService.show({
            title: t('Validation Error'),
            message: t(
              'Studies with "Awaiting Review" or "Approved" report status cannot be reassigned to another hub facility.'
            ),
            type: 'error',
            duration: 5000,
          });
        }
        return;
      }
    }

    show({
      content: AssignHubFacilityModal,
      contentProps: {
        mode: isReassign ? 'reassign' : 'assign',
        checkedStudyIds: Array.from(checkedStudies),
        selectedStudies: selectedStudies.map(study => ({
          studyInstanceUid: study.studyInstanceUid,
          modalities: study.modalities || '',
          assignedName: study.assignedName || '',
          assignedHubFacilityId: study.assignedHubFacilityId || '',
          studyId: study.studyId || null,
          reportStatus: study.reportStatus || '',
          patientName: study.patientName,
          studyDescription: study.description,
          patientId: study.mrn,
          accessionNumber: study.accession,
          priority: study.priority,
          isCritical: study.isCritical,
        })),
        linkedHubFacilities,
        servicesManager,
        onSuccess: () => {
          setCheckedStudies(new Set());
          if (onRefresh) {
            onRefresh();
          }
        },
        onClose: () => hide(),
      },
      title: '',
      containerClassName:
        'max-w-lg w-[calc(100vw-2rem)] !bg-white p-0 gap-0 rounded-lg border border-[#E0E0E0] text-[#000000] shadow-lg sm:max-w-[520px]',
    });
  };

  unassignHubStudiesRef.current = async () => {
    const selectedStudies = sortedStudies.filter(study =>
      checkedStudies.has(study.studyInstanceUid)
    );

    if (selectedStudies.length === 0) {
      return;
    }

    const hubAssignmentState = getSelectionHubAssignmentState(selectedStudies);
    const { uiNotificationService } = servicesManager.services;

    if (hubAssignmentState === 'mixed') {
      if (uiNotificationService) {
        uiNotificationService.show({
          title: t('Validation Error'),
          message: t(
            'This action cannot be performed on mixed selections. Please choose either hub-assigned or hub-unassigned studies.'
          ),
          type: 'error',
          duration: 5000,
        });
      }
      return;
    }

    if (hubAssignmentState !== 'all') {
      if (uiNotificationService) {
        uiNotificationService.show({
          title: t('Validation Error'),
          message: t('Please select only hub-assigned studies to unassign from hub.'),
          type: 'error',
          duration: 5000,
        });
      }
      return;
    }

    try {
      const result = await batchHubRoutingStudies({
        action: 'unassign-hub',
        studies: selectedStudies.map(study => ({
          study_instance_uid: study.studyInstanceUid,
        })),
      });

      const succeeded = result.success.length;
      const failed = result.failed.length;

      if (uiNotificationService) {
        if (failed === 0) {
          uiNotificationService.show({
            title: t('Success'),
            message: t('Successfully unassigned {{count}} study(ies) from hub facility', {
              count: succeeded,
            }),
            type: 'success',
            duration: 5000,
          });
        } else if (succeeded > 0) {
          uiNotificationService.show({
            title: t('Partial Success'),
            message: t('Unassigned {{success}} study(ies) from hub; {{failed}} failed', {
              success: succeeded,
              failed,
            }),
            type: 'warning',
            duration: 5000,
          });
        } else {
          throw new Error(
            result.failed[0]?.error || t('Failed to unassign studies from hub facility')
          );
        }
      }

      setCheckedStudies(new Set());
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t('Failed to unassign studies from hub facility');
      if (uiNotificationService) {
        uiNotificationService.show({
          title: t('Unassign Failed'),
          message: errorMessage,
          type: 'error',
          duration: 5000,
        });
      }
    }
  };

  unassignStudiesRef.current = async () => {
    const selectedStudies = sortedStudies.filter(study =>
      checkedStudies.has(study.studyInstanceUid)
    );

    const approvedStudies = selectedStudies.filter(
      study => study.reportStatus && study.reportStatus.trim().toLowerCase() === 'approved'
    );

    if (approvedStudies.length > 0) {
      const { uiNotificationService } = servicesManager.services;
      if (uiNotificationService) {
        uiNotificationService.show({
          title: t('Validation Error'),
          message: t(
            'This action cannot be performed on mixed selections. Please choose either Assigned or Unassigned studies.'
          ),
          type: 'error',
          duration: 5000,
          allowDuplicates: true,
        });
      }
      return;
    }

    const assignedCount = selectedStudies.filter(
      study => study.assignedName && study.assignedName.trim() !== ''
    ).length;
    const unassignedCount = selectedStudies.length - assignedCount;

    if (assignedCount > 0 && unassignedCount > 0) {
      const { uiNotificationService } = servicesManager.services;
      if (uiNotificationService) {
        uiNotificationService.show({
          title: t('Validation Error'),
          message: t(
            'This action cannot be performed on mixed selections. Please choose either Assigned or Unassigned studies.'
          ),
          type: 'error',
          duration: 5000,
        });
      }
      return;
    }

    if (unassignedCount > 0) {
      const { uiNotificationService } = servicesManager.services;
      if (uiNotificationService) {
        uiNotificationService.show({
          title: t('Validation Error'),
          message: t('Please select only assigned studies to unassign.'),
          type: 'error',
          duration: 5000,
        });
      }
      return;
    }

    const checkedStudyIds = Array.from(checkedStudies);

    try {
      const { API_ENDPOINTS, STORAGE_KEYS } = await import('../../custom/config/api.config');
      const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const studiesForUnassign = selectedStudies
        .filter(study => checkedStudyIds.includes(study.studyInstanceUid))
        .map(study => {
          const studyId = study.studyId;
          if (!studyId || studyId === null || studyId === undefined || studyId === '') {
            throw new Error(
              `Study ${study.studyInstanceUid} does not have a study_id and cannot be unassigned`
            );
          }
          return { study_id: Number(studyId) };
        });

      const batchPayload = {
        status: 'UNASSIGNED',
        studies: studiesForUnassign,
      };

      const response = await fetch(API_ENDPOINTS.STUDIES.REASSIGN, {
        method: 'PUT',
        headers,
        body: JSON.stringify(batchPayload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to unassign studies: ${response.statusText}`);
      }

      const responseData = await response.json();
      if (!responseData.success) {
        throw new Error(responseData.message || 'Failed to unassign studies');
      }

      const { data } = responseData;
      const succeededCount = data?.summary?.succeeded || data?.success?.length || 0;
      const failedCount = data?.summary?.failed || data?.failed?.length || 0;

      const { uiNotificationService } = servicesManager.services;
      if (uiNotificationService) {
        if (failedCount > 0) {
          uiNotificationService.show({
            title: t('Partial Success'),
            message: t(
              'Successfully unassigned {{succeeded}} study(ies). {{failed}} study(ies) failed.',
              {
                succeeded: succeededCount,
                failed: failedCount,
              }
            ),
            type: 'warning',
            duration: 5000,
          });
        } else {
          uiNotificationService.show({
            title: t('Success'),
            message: t('Successfully unassigned {{count}} study(ies)', {
              count: succeededCount,
            }),
            type: 'success',
            duration: 5000,
          });
        }
      }

      setCheckedStudies(new Set());
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      console.error('Error unassigning studies:', err);
      const { uiNotificationService } = servicesManager.services;
      if (uiNotificationService) {
        uiNotificationService.show({
          title: t('Unassignment Failed'),
          message: err instanceof Error ? err.message : t('Failed to unassign studies'),
          type: 'error',
          duration: 5000,
        });
      }
    }
  };

  return (
    <ResponsiveLayoutProvider showAnalyticsSubSidebar={false}>
      <div
        className={classnames(
          'page-layout custom-studies',
          isImagingWorklist ? 'overflow-hidden bg-white' : 'bg-black'
        )}
      >
        <AppSidebar menuItems={sidebarMenuItems} />
        <SidebarBackdrop />
        <div className="page-layout-main">
          <PageHeader
            title={isImagingWorklist ? '' : 'Studies'}
            variant={isImagingWorklist ? 'light' : 'default'}
            breadcrumbItems={
              isImagingWorklist
                ? [{ label: 'Home', to: '/' }, { label: 'Imaging Worklist' }]
                : undefined
            }
          />
          <Onboarding />
          <div
            className={classnames(
              'flex flex-col',
              isImagingWorklist ? 'min-h-0 flex-1 overflow-hidden bg-white' : 'min-h-0 flex-1 overflow-hidden bg-black'
            )}
          >
          {isImagingWorklist ? (
            <div className="ohif-imaging-worklist flex min-h-0 min-w-0 flex-1 flex-col">
              <div className="imaging-worklist-sheet flex min-h-0 min-w-0 flex-1 flex-col px-2 sm:px-4">
                <ImagingWorklistToolbar
                  layout={imagingLayout}
                  filterValues={{
                    patientName: filterValues.patientName,
                    studyDate: filterValues.studyDate,
                    modalities: filterValues.modalities,
                  }}
                  onMergeFilterValues={mergeWorklistServerFilters}
                  imagingListFilters={imagingListFilters}
                  onImagingListFiltersChange={handleImagingListFiltersChange}
                  modalityOptions={modalityOptions}
                  radiologistOptions={radiologistOptionsFromApi}
                  showRadiologistFilter={imagingLayout === 'adminTech'}
                  showAssignActions={canAssignRadiologist}
                  showAssignHubAction={canAssignToHubFacility}
                  hasCheckedStudies={checkedStudies.size > 0 && canAssignRadiologist}
                  checkedStudiesCount={checkedStudies.size}
                  hasAssignedStudies={selectionHasAssignedStudies(sortedStudies, checkedStudies)}
                  hubAssignmentSelectionState={hubAssignmentSelectionState}
                  onAssignClick={() => openAssignModalRef.current()}
                  onAssignHubClick={() => openAssignHubModalRef.current()}
                  onUnassignClick={() => unassignStudiesRef.current()}
                  onUnassignHubClick={() => unassignHubStudiesRef.current()}
                  showPeerReviewAssignAction={selectionIsAllApprovedForPeerReview}
                  onPeerReviewAssignClick={() => openAssignPeerReviewModalRef.current()}
                  columnVisibility={columnVisibility}
                  onColumnVisibilityChange={next => {
                    setColumnVisibility(next);
                    persistImagingColumnVisibility(next);
                  }}
                  showAssignedColumn={showAssignedWorklistColumn}
                  hubSpokeColumnKey={hubSpokeWorklistColumnKey}
                />
                {/* Table + pagination in one block: footer sits under last row (Figma). Scroll only when content exceeds viewport. */}
                <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-white">
                  {hasStudies || isLoadingData ? (
                    <div className="imaging-worklist-main-scroll imaging-worklist-main-scroll--loading-host bg-white">
                      {!isLoadingData && hasStudies ? (
                        <>
                          <div className="imaging-worklist-table-x-scroll">
                            <ImagingWorklistTable
                              layout={imagingLayout}
                              studies={imagingDisplayStudies}
                              pageOffset={imagingTablePageOffset}
                              showCheckboxes={imagingLayout === 'adminTech' && canAssignRadiologist}
                              columnVisibility={columnVisibility}
                              checkedStudies={checkedStudies}
                              hubFacilityNameById={hubFacilityNameById}
                              hubSpokeColumnKey={hubSpokeWorklistColumnKey}
                              onToggleStudyCheck={(studyInstanceUid, checked) => {
                                setCheckedStudies(prev => {
                                  const next = new Set(prev);
                                  if (checked) {
                                    next.add(studyInstanceUid);
                                  } else {
                                    next.delete(studyInstanceUid);
                                  }
                                  return next;
                                });
                              }}
                              onToggleAllPageStudies={
                                imagingLayout === 'adminTech' && canAssignRadiologist
                                  ? checked => {
                                      setCheckedStudies(prev => {
                                        const next = new Set(prev);
                                        imagingDisplayStudies.forEach(s => {
                                          if (checked) {
                                            next.add(s.studyInstanceUid);
                                          } else {
                                            next.delete(s.studyInstanceUid);
                                          }
                                        });
                                        return next;
                                      });
                                    }
                                  : undefined
                              }
                              getStudyLaunchHref={study =>
                                getStudyViewerHref({
                                  study,
                                  appConfig,
                                  dataPath: dataPath || '',
                                  filterValues,
                                  preserveQueryParameters,
                                })
                              }
                              onRadiologistOpenStudy={study => {
                                const href = getStudyViewerHref({
                                  study,
                                  appConfig,
                                  dataPath: dataPath || '',
                                  filterValues,
                                  preserveQueryParameters,
                                });
                                if (href) {
                                  navigate(href);
                                }
                              }}
                            />
                          </div>
                          <ImagingWorklistPagination
                            currentPage={pageNumber}
                            perPage={resultsPerPage}
                            totalItems={
                              usesServerStudyPagination
                                ? studiesTotal
                                : studiesAfterClientFilters.length
                            }
                            onChangePage={onPageNumberChange}
                            onChangePerPage={onResultsPerPageChange}
                          />
                        </>
                      ) : null}
                      {appConfig.showLoadingIndicator && isLoadingData ? (
                        <LoadingIndicatorProgress className="imaging-worklist-loading-overlay h-full w-full bg-white" />
                      ) : null}
                    </div>
                  ) : (
                    <div className="flex min-h-0 flex-1 flex-col items-center justify-center bg-white pt-48">
                      <EmptyStudies />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="study-list-viewport flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
              <div className="study-list-viewport__scroll min-h-0 min-w-0 flex-1 overflow-auto">
                <div className="study-list-viewport__content">
                  <StudyListFilter
                    numOfStudies={pageNumber * resultsPerPage > 100 ? 101 : numOfStudies}
                    filtersMeta={filtersMeta}
                    filterValues={{ ...filterValues, ...defaultSortValues }}
                    onChange={setFilterValues}
                    clearFilters={() => setFilterValues(defaultFilterValues)}
                    isFiltering={isFiltering(filterValues, defaultFilterValues)}
                    onUploadClick={uploadProps ? () => show(uploadProps) : undefined}
                    getDataSourceConfigurationComponent={
                      dataSourceConfigurationComponent
                        ? () => dataSourceConfigurationComponent()
                        : undefined
                    }
                    hasCheckedStudies={checkedStudies.size > 0 && canAssignRadiologist}
                    checkedStudiesCount={checkedStudies.size}
                    hasAssignedStudies={selectionHasAssignedStudies(sortedStudies, checkedStudies)}
                    onActionButtonClick={() => openAssignModalRef.current()}
                    onUnassignButtonClick={() => unassignStudiesRef.current()}
                  />
                  <StudyListTable
                    tableDataSource={
                      hasStudies ? tableDataSource.slice(offset, offsetAndTake) : []
                    }
                    numOfStudies={numOfStudies}
                    querying={querying}
                    filtersMeta={filtersMeta}
                    filterValues={{ ...filterValues, ...defaultSortValues }}
                    onFilterChange={setFilterValues}
                    isSortingEnabled={numOfStudies > 0 && numOfStudies <= 100}
                  />
                  {hasStudies ? (
                    <StudyListPagination
                      onChangePage={onPageNumberChange}
                      onChangePerPage={onResultsPerPageChange}
                      currentPage={pageNumber}
                      perPage={resultsPerPage}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center py-48">
                      {appConfig.showLoadingIndicator && isLoadingData ? (
                        <LoadingIndicatorProgress className="h-full w-full bg-black" />
                      ) : (
                        <EmptyStudies />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </ResponsiveLayoutProvider>
  );
}

WorkList.propTypes = {
  data: PropTypes.array.isRequired,
  dataSource: PropTypes.shape({
    query: PropTypes.object.isRequired,
    getConfig: PropTypes.func,
  }).isRequired,
  isLoadingData: PropTypes.bool.isRequired,
  servicesManager: PropTypes.object.isRequired,
};

const defaultFilterValues = {
  patientName: '',
  mrn: '',
  studyDate: {
    startDate: null,
    endDate: null,
  },
  description: '',
  modalities: [],
  accession: '',
  sortBy: '',
  sortDirection: 'none',
  pageNumber: 1,
  resultsPerPage: 10,
  datasources: '',
};

function _tryParseInt(str, defaultValue) {
  let retValue = defaultValue;
  if (str && str.length > 0) {
    if (!isNaN(str)) {
      retValue = parseInt(str);
    }
  }
  return retValue;
}

function _getQueryFilterValues(params) {
  const newParams = new URLSearchParams();
  for (const [key, value] of params) {
    newParams.set(key.toLowerCase(), value);
  }
  params = newParams;

  const queryFilterValues = {
    patientName: params.get('patientname'),
    mrn: params.get('mrn'),
    studyDate: {
      startDate: params.get('startdate') || null,
      endDate: params.get('enddate') || null,
    },
    description: params.get('description'),
    modalities: params.get('modalities') ? params.get('modalities').split(',') : [],
    accession: params.get('accession'),
    sortBy: params.get('sortby'),
    sortDirection: params.get('sortdirection'),
    pageNumber: _tryParseInt(params.get('pagenumber'), undefined),
    resultsPerPage: _tryParseInt(params.get('resultsperpage'), undefined),
    datasources: params.get('datasources'),
    configUrl: params.get('configurl'),
  };

  // Delete null/undefined keys
  Object.keys(queryFilterValues).forEach(
    key => queryFilterValues[key] == null && delete queryFilterValues[key]
  );

  return queryFilterValues;
}

function _sortStringDates(s1, s2, sortModifier) {
  // TODO: Delimiters are non-standard. Should we support them?
  const s1Date = moment(s1.date, ['YYYYMMDD', 'YYYY.MM.DD'], true);
  const s2Date = moment(s2.date, ['YYYYMMDD', 'YYYY.MM.DD'], true);

  if (s1Date.isValid() && s2Date.isValid()) {
    return (s1Date.toISOString() > s2Date.toISOString() ? 1 : -1) * sortModifier;
  } else if (s1Date.isValid()) {
    return sortModifier;
  } else if (s2Date.isValid()) {
    return -1 * sortModifier;
  }
}

export default WorkList;
